import { useEffect, useState } from "react"
import { supabase } from "@/integrations/supabase/client"
import { Card, CardContent } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { Edit } from "lucide-react"
import { useNavigate } from "react-router-dom"
import { useIsMobile } from "@/hooks/use-mobile"
import { useIsAdmin } from "@/hooks/useUserRole"

type BannerType = "INTRODUCTION" | "LEARNING" | "QUESTIONS" | "FEED"
type DisplayType = "MOBILE" | "DESKTOP"

interface Banner {
  id: string
  image_url: string
  type: BannerType
  display: DisplayType
  is_active: boolean
  created_at: string
}

export function CommunityBanner({ type }: { type: BannerType }) {
  const [banner, setBanner] = useState<Banner | null>(null)
  const { isAdmin } = useIsAdmin()
  const isMobile = useIsMobile()
  const displayType: DisplayType = isMobile ? "MOBILE" : "DESKTOP"

  const fetchBanner = async () => {
    const { data, error } = await supabase
      .from('community_banners')
      .select('id, image_url, type, display, is_active, created_at')
      .eq('is_active', true)
      .eq('type', type)
      .eq('display', displayType)
      .order('created_at', { ascending: false })
      .limit(1)

    if (data && data.length > 0) {
      setBanner(data[0] as Banner)
    }

    if (error) {
      console.error('Error fetching banner:', error)
    }
  }

  useEffect(() => {
    fetchBanner()
  }, [type, displayType])

  const navigate = useNavigate()

  const handleEditBanner = async () => {
    if (!banner) return;
    navigate(`/candidate/admin/banners`);
  };

  if (!banner?.image_url) return null;

  return (
    <Card className="mb-6 bg-gradient-to-r from-primary/10 to-primary/5">
      <CardContent className="p-3 sm:p-6 relative">
        <img
          src={banner.image_url}
          alt="Banner informativo da comunidade de dados"
          className="rounded-lg w-full aspect-square md:aspect-[1200/484] h-auto object-cover"
          loading="lazy"
          width={isMobile ? "640" : "1200"}
          height={isMobile ? "640" : "484"}
        />
        {isAdmin && (
          <Button
            variant="outline"
            size="icon"
            className="absolute top-5 right-5 sm:top-8 sm:right-8"
            onClick={handleEditBanner}
          >
            <Edit className="h-4 w-4" />
          </Button>
        )}
      </CardContent>
    </Card>
  )
}